<script setup lang="ts">
import { computed } from 'vue'
import { NuxtLink } from '#components'

interface Props {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'link'
  size?: 'sm' | 'md' | 'lg'
  to?: string
  href?: string
  block?: boolean
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
  block: false,
  disabled: false,
  loading: false
})

const baseStyles = 'inline-flex items-center justify-center font-bold uppercase tracking-widest transition-all duration-300 focus:outline-hidden disabled:opacity-50 disabled:cursor-not-allowed clip-button'

const variants = {
  primary: 'bg-oxo-red text-white hover:bg-foreground hover:text-background shadow-lg hover:shadow-[0_0_20px_rgba(var(--oxo-red-rgb),0.4)]',
  secondary: 'bg-oxo-yellow text-black hover:bg-foreground hover:text-background shadow-lg hover:shadow-[0_0_20px_rgba(var(--oxo-yellow-rgb),0.4)]',
  outline: 'border-2 border-oxo-border text-foreground hover:bg-foreground hover:text-background backdrop-blur-xs',
  ghost: 'text-foreground-dim hover:text-oxo-red hover:bg-foreground/5 bg-transparent shadow-none',
  link: 'text-white/70 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 shadow-none'
}

const sizes = {
  sm: 'text-[10px] px-4 py-2',
  md: 'text-xs px-6 py-3',
  lg: 'text-sm px-8 py-4'
}

const classes = computed(() => {
  return [
    baseStyles,
    variants[props.variant],
    sizes[props.size],
    props.block ? 'w-full' : '',
    props.loading ? 'cursor-wait' : ''
  ].join(' ')
})

const isLink = computed(() => !!props.to || !!props.href)
const componentTag = computed(() => isLink.value ? NuxtLink : 'button')
</script>

<template>
  <component
    :is="componentTag"
    :to="to"
    :href="href"
    :type="!isLink ? type : undefined"
    :disabled="disabled || loading"
    :class="classes"
  >
    <span v-if="loading" class="mr-2 animate-spin">
      <i class="fas fa-circle-notch" aria-hidden="true"></i>
    </span>
    <slot />
    <span v-if="$slots.icon" class="ml-2">
      <slot name="icon" />
    </span>
  </component>
</template>
